import type { AddressWords } from './address';
import { grandAddress, indicPack, stepNoun, type IndicGrammar, type IndicWords, type UncleKind } from './indic';

/**
 * Hindi relationship words (PRD 7.2.2), in Devanagari and in English letters. Beta until reviewed. Hindi
 * names a father's elder brother tau and a younger one chacha, and first cousins by which uncle or aunt is
 * their parent (chachera, phuphera, mamera, mausera).
 */

const COUSIN_HI: Record<UncleKind, [string, string]> = {
  FB: ['चचेरा भाई', 'चचेरी बहन'],
  FZ: ['फुफेरा भाई', 'फुफेरी बहन'],
  MB: ['ममेरा भाई', 'ममेरी बहन'],
  MZ: ['मौसेरा भाई', 'मौसेरी बहन'],
};
const COUSIN_LATN: Record<UncleKind, [string, string]> = {
  FB: ['Chachera Bhai', 'Chacheri Behen'],
  FZ: ['Phuphera Bhai', 'Phupheri Behen'],
  MB: ['Mamera Bhai', 'Mameri Behen'],
  MZ: ['Mausera Bhai', 'Mauseri Behen'],
};
const cousin = (table: Record<UncleKind, [string, string]>) => (kind: UncleKind, sex: 'f' | 'm' | null) => {
  const [m, f] = table[kind];
  return sex === 'f' ? f : sex === 'm' ? m : `${m}/${f}`;
};

const hi: IndicWords = {
  you: 'आप',
  relative: 'रिश्तेदार',
  spouse: { m: 'पति', f: 'पत्नी' },
  parent: { m: 'पापा', f: 'मम्मी' },
  child: { m: 'बेटा', f: 'बेटी' },
  brother: { elder: 'भैया', younger: 'भाई', any: 'भाई' },
  sister: { elder: 'दीदी', younger: 'बहन', any: 'बहन' },
  grandF: { m: 'दादा', f: 'दादी' },
  grandM: { m: 'नाना', f: 'नानी' },
  greatF: { m: 'परदादा', f: 'परदादी' },
  greatM: { m: 'परनाना', f: 'परनानी' },
  ancestor: 'पूर्वज',
  grandchildViaSon: { m: 'पोता', f: 'पोती' },
  grandchildViaDaughter: { m: 'नाती', f: 'नातिन' },
  greatGrandchild: { m: 'परपोता', f: 'परपोती' },
  descendant: 'वंशज',
  uncle: { FB: { elder: 'ताऊ', any: 'चाचा' }, FZ: { elder: 'बुआ', any: 'बुआ' }, MB: { elder: 'मामा', any: 'मामा' }, MZ: { elder: 'मौसी', any: 'मौसी' } },
  uncleSpouse: { FB: { elder: 'ताई', any: 'चाची' }, FZ: { elder: 'फूफा', any: 'फूफा' }, MB: { elder: 'मामी', any: 'मामी' }, MZ: { elder: 'मौसा', any: 'मौसा' } },
  cousin: cousin(COUSIN_HI),
  distantCousin: { m: 'दूर के भाई', f: 'दूर की बहन' },
  nephewViaBrother: { m: 'भतीजा', f: 'भतीजी' },
  nephewViaSister: { m: 'भांजा', f: 'भांजी' },
  parentInLaw: { m: 'ससुर', f: 'सास' },
  grandparentInLaw: { m: 'दादा ससुर', f: 'दादी सास' },
  spouseUncle: (kind) => ({ FB: 'चाचा ससुर', FZ: 'बुआ सास', MB: 'मामा ससुर', MZ: 'मौसी सास' })[kind],
  husbandBrother: { elder: 'जेठ', younger: 'देवर', any: 'जेठ/देवर' },
  husbandSister: 'ननद',
  wifeBrother: 'साला',
  wifeSister: 'साली',
  husbandBrotherWife: { elder: 'जेठानी', younger: 'देवरानी', any: 'जेठानी/देवरानी' },
  husbandSisterHusband: 'ननदोई',
  wifeSisterHusband: 'साढ़ू',
  wifeBrotherWife: 'सलहज',
  brotherWife: 'भाभी',
  sisterHusband: { elder: 'जीजा', younger: 'बहनोई', any: 'जीजा' },
  childInLaw: { m: 'दामाद', f: 'बहू' },
  coParentInLaw: { m: 'समधी', f: 'समधन' },
  stepParent: { m: 'सौतेले पिता', f: 'सौतेली माँ' },
  stepChild: { m: 'सौतेला बेटा', f: 'सौतेली बेटी' },
  stepSibling: { m: 'सौतेला भाई', f: 'सौतेली बहन' },
};

const hiLatn: IndicWords = {
  you: 'Aap',
  relative: 'Rishtedar',
  spouse: { m: 'Pati', f: 'Patni' },
  parent: { m: 'Papa', f: 'Mummy' },
  child: { m: 'Beta', f: 'Beti' },
  brother: { elder: 'Bhaiya', younger: 'Bhai', any: 'Bhai' },
  sister: { elder: 'Didi', younger: 'Behen', any: 'Behen' },
  grandF: { m: 'Dada', f: 'Dadi' },
  grandM: { m: 'Nana', f: 'Nani' },
  greatF: { m: 'Pardada', f: 'Pardadi' },
  greatM: { m: 'Parnana', f: 'Parnani' },
  ancestor: 'Purvaj',
  grandchildViaSon: { m: 'Pota', f: 'Poti' },
  grandchildViaDaughter: { m: 'Nati', f: 'Natin' },
  greatGrandchild: { m: 'Parpota', f: 'Parpoti' },
  descendant: 'Vanshaj',
  uncle: { FB: { elder: 'Tau', any: 'Chacha' }, FZ: { elder: 'Bua', any: 'Bua' }, MB: { elder: 'Mama', any: 'Mama' }, MZ: { elder: 'Mausi', any: 'Mausi' } },
  uncleSpouse: { FB: { elder: 'Tai', any: 'Chachi' }, FZ: { elder: 'Phupha', any: 'Phupha' }, MB: { elder: 'Mami', any: 'Mami' }, MZ: { elder: 'Mausa', any: 'Mausa' } },
  cousin: cousin(COUSIN_LATN),
  distantCousin: { m: 'Door ke Bhai', f: 'Door ki Behen' },
  nephewViaBrother: { m: 'Bhatija', f: 'Bhatiji' },
  nephewViaSister: { m: 'Bhanja', f: 'Bhanji' },
  parentInLaw: { m: 'Sasur', f: 'Saas' },
  grandparentInLaw: { m: 'Dada Sasur', f: 'Dadi Saas' },
  spouseUncle: (kind) => ({ FB: 'Chacha Sasur', FZ: 'Bua Saas', MB: 'Mama Sasur', MZ: 'Mausi Saas' })[kind],
  husbandBrother: { elder: 'Jeth', younger: 'Devar', any: 'Jeth/Devar' },
  husbandSister: 'Nanad',
  wifeBrother: 'Sala',
  wifeSister: 'Saali',
  husbandBrotherWife: { elder: 'Jethani', younger: 'Devrani', any: 'Jethani/Devrani' },
  husbandSisterHusband: 'Nandoi',
  wifeSisterHusband: 'Saadhu',
  wifeBrotherWife: 'Salhaj',
  brotherWife: 'Bhabhi',
  sisterHusband: { elder: 'Jija', younger: 'Behnoi', any: 'Jija' },
  childInLaw: { m: 'Damad', f: 'Bahu' },
  coParentInLaw: { m: 'Samdhi', f: 'Samdhan' },
  stepParent: { m: 'Sautele Pita', f: 'Sauteli Maa' },
  stepChild: { m: 'Sautela Beta', f: 'Sauteli Beti' },
  stepSibling: { m: 'Sautela Bhai', f: 'Sauteli Behen' },
};

/** Possessives agree with the next noun: ka (m), ki (f), ke (honorific, or before another possessive). */
function grammar(latin: boolean): IndicGrammar {
  const w = (word: string, oblique = word, honorific = false) => ({ word, oblique, honorific });
  const forms = latin
    ? {
        parent: { m: w('papa', 'papa', true), f: w('mummy') },
        child: { m: w('beta', 'bete'), f: w('beti') },
        sibling: { m: w('bhai'), f: w('behen') },
        spouse: { m: w('pati', 'pati', true), f: w('patni') },
      }
    : {
        parent: { m: w('पापा', 'पापा', true), f: w('मम्मी') },
        child: { m: w('बेटा', 'बेटे'), f: w('बेटी') },
        sibling: { m: w('भाई'), f: w('बहन') },
        spouse: { m: w('पति', 'पति', true), f: w('पत्नी') },
      };
  const [ka, ki, ke] = latin ? ['ka', 'ki', 'ke'] : ['का', 'की', 'के'];
  return {
    noun: (step) => stepNoun(step, forms),
    your: (fem) => (latin ? (fem ? 'aapki' : 'aapke') : fem ? 'आपकी' : 'आपके'),
    of: (fem, last, honorific) => (fem ? ki : honorific || !last ? ke : ka),
    attach: false,
    generation: (delta) => {
      if (delta === 0) return latin ? 'Aapki peedhi' : 'आपकी पीढ़ी';
      if (delta === -1) return latin ? 'Mata-pita' : 'माता-पिता';
      if (delta === 1) return latin ? 'Santan' : 'संतान';
      const n = Math.abs(delta);
      if (latin) return `${n} peedhi ${delta < 0 ? 'upar' : 'neeche'}`;
      return `${n} पीढ़ी ${delta < 0 ? 'ऊपर' : 'नीचे'}`;
    },
  };
}

/**
 * Forms of address (PRD 7.2.4). Seniors get the kin term, often after the name ("Lali bhai", "Kamala didi", "Ram
 * chacha"); juniors by age or generation are called by name (Mehrotra 1985). -ji for respect; Badi / Bade for a
 * parent's elder sister or brother (Balbir & Singh 1986). Sources: docs/kinship-languages-plan.md 16.2 and 16.7.
 */
const hiAddress: AddressWords = {
  nameScript: /^\p{Script=Devanagari}+$/u,
  parent: { m: ['पापा', 'पापाजी', 'पिताजी'], f: ['मम्मी', 'माँ', 'मम्मीजी'] },
  grandparent: grandAddress([['दादाजी', 'दादा'], ['दादीजी', 'दादी'], ['नानाजी', 'नाना'], ['नानीजी', 'नानी']], [['परदादाजी'], ['परदादीजी'], ['परनानाजी'], ['परनानीजी']]),
  elder: { m: ['{name} भाई', '{name} भैया', 'भैया', 'भाईसाहब'], f: ['{name} दीदी', 'दीदी', 'जीजी'] },
  uncle: {
    FB: { elder: ['ताऊजी'], any: ['चाचाजी', 'चाचू', '{name} चाचा'] },
    FZ: { elder: ['बड़ी बुआजी', 'बुआजी'], any: ['बुआजी', 'बुआ'] },
    MB: { elder: ['बड़े मामाजी', 'मामाजी'], any: ['मामाजी', 'मामू'] },
    MZ: { elder: ['बड़ी मौसीजी', 'मौसीजी'], any: ['मौसीजी', 'मौसी'] },
  },
  uncleSpouse: {
    FB: { elder: ['ताईजी'], any: ['चाचीजी', 'चाची', '{name} चाची'] },
    FZ: { elder: ['बड़े फूफाजी', 'फूफाजी'], any: ['फूफाजी', 'फूफा'] },
    MB: { elder: ['बड़ी मामीजी', 'मामीजी'], any: ['मामीजी', 'मामी'] },
    MZ: { elder: ['बड़े मौसाजी', 'मौसाजी'], any: ['मौसाजी', 'मौसा'] },
  },
  parentInLaw: { m: ['पापाजी', 'बाबूजी'], f: ['मम्मीजी', 'माँजी'] },
  elderInLaw: (sex, spouseSex) =>
    spouseSex === 'm' ? (sex === 'f' ? ['जीजी', 'दीदी'] : ['भाईसाहब', 'जेठजी']) : sex === 'f' ? ['दीदी', '{name}जी'] : ['{name}जी', 'भैया'],
  elderSiblingSpouse: { m: ['जीजाजी', 'जीजा'], f: ['{name} भाभी', 'भाभी', 'भाभीजी'] },
  coParentInLaw: { m: ['समधीजी'], f: ['समधनजी'] },
};
const hiLatnAddress: AddressWords = {
  parent: { m: ['Papa', 'Papaji', 'Pitaji'], f: ['Mummy', 'Maa', 'Mummyji'] },
  grandparent: grandAddress([['Dadaji', 'Dada'], ['Dadiji', 'Dadi'], ['Nanaji', 'Nana'], ['Naniji', 'Nani']], [['Pardadaji'], ['Pardadiji'], ['Parnanaji'], ['Parnaniji']]),
  elder: { m: ['{name} Bhai', '{name} Bhaiya', 'Bhaiya', 'Bhaisahab'], f: ['{name} Didi', 'Didi', 'Jiji'] },
  uncle: {
    FB: { elder: ['Tauji'], any: ['Chachaji', 'Chachu', '{name} Chacha'] },
    FZ: { elder: ['Badi Buaji', 'Buaji'], any: ['Buaji', 'Bua'] },
    MB: { elder: ['Bade Mamaji', 'Mamaji'], any: ['Mamaji', 'Mamu', '{name} Mama'] },
    MZ: { elder: ['Badi Mausiji', 'Mausiji'], any: ['Mausiji', 'Mausi'] },
  },
  uncleSpouse: {
    FB: { elder: ['Taiji'], any: ['Chachiji', 'Chachi', '{name} Chachi'] },
    FZ: { elder: ['Bade Phuphaji', 'Phuphaji'], any: ['Phuphaji', 'Phupha'] },
    MB: { elder: ['Badi Mamiji', 'Mamiji'], any: ['Mamiji', 'Mami'] },
    MZ: { elder: ['Bade Mausaji', 'Mausaji'], any: ['Mausaji', 'Mausa'] },
  },
  parentInLaw: { m: ['Papaji', 'Babuji'], f: ['Mummyji', 'Maaji'] },
  elderInLaw: (sex, spouseSex) =>
    spouseSex === 'm' ? (sex === 'f' ? ['Jiji', 'Didi'] : ['Bhaisahab', 'Jethji']) : sex === 'f' ? ['Didi', '{name}ji'] : ['{name}ji', 'Bhaiya'],
  elderSiblingSpouse: { m: ['Jijaji', 'Jija'], f: ['{name} Bhabhi', 'Bhabhi', 'Bhabhiji'] },
  coParentInLaw: { m: ['Samdhiji'], f: ['Samdhanji'] },
};

export const hiPack = indicPack({ id: 'hi', name: 'हिन्दी', lang: 'hi' }, hi, grammar(false), false, hiAddress);
export const hiLatnPack = indicPack({ id: 'hi-Latn', name: 'Hindi (chacha, mausi)', lang: 'hi-Latn' }, hiLatn, grammar(true), true, hiLatnAddress);
